import { isSolidFramingFloor } from "building-generation/elements/floor/floor";
import { extrudeFloorFramingPlacements } from "building-generation/elements/floor/framingUtils";
import { floorFramingFaces } from "building-generation/elements/structural-framing/floor/floorFramingUtils";
import { generatedBuildingEntityHandle } from "building-types/entities/buildingEntityPickHandle";
import type { FloorSystem } from "building-types/floors/floorSystemTypes";
import { FloorType } from "building-types/floors/floorTypes";
import {
  FloorFramingComponentType,
  floorFramingComponentTypeFromJoistPlacement,
} from "building-types/framing/floorFramingComponentTypes";
import type { LumberRail } from "building-types/framing/lumberTypes";
import type { BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { GeneratedFramedFloorAssembly } from "building-types/horizontalAssemblies/generatedFloorAssemblyTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import type { Room } from "building-types/rooms/roomTypes";
import type { Table } from "common/data/table";
import { MS_Color__C, MS_Color_PlanFloor__Default } from "common/materials/library/designMaterialSrcDefaults";
import { DMC } from "common/materials/library/DMC";
import type { MaterialSrc } from "common/materials/materialSrc";
import { faceGroupNew } from "geom/brep";
import { polyLine3EvalEdge } from "geom/polyLine3";
import { UVGeneration } from "geom/uv";
import { buildExteriorLayers } from "./buildingGeometryTools";
import type { FloorRoomRegion } from "./floorReportingUnitAttribution";
import { floorRegionForPoint, floorRoomRegions } from "./floorReportingUnitAttribution";

export function getFloorFramingGeometries(
  floorSystems: Table<FloorSystem>,
  framedFloorAssemblies: Table<GeneratedFramedFloorAssembly>,
  rooms: Table<Room>,
  roomsByLevel: Table<string[]>,
  emitter: BuildingGeometryEmitter,
) {
  getFloorFramingGeometriesFromFloorSystems(floorSystems, rooms, roomsByLevel, emitter);

  getFloorFramingGeometriesFromFramedFloorAssemblies(framedFloorAssemblies, rooms, roomsByLevel, emitter);
}

interface FramingMemberAttribution {
  readonly pickHandle: DocEntityHandle;
  readonly reportingUnitHandle: DocEntityHandle | undefined;
}

/**
 * Attributes a framing member by its midpoint. Floors and floor assemblies are grouped by
 * construction parameters, not by unit or by component instance, so one of them can span several of
 * either; each member is resolved individually against the level's room regions. `fallbackHandle` is
 * the owning floor's doc entity handle, used when the level has no rooms to resolve against.
 *
 * A member inside a component instance is selected as the instance, so it highlights with the rest
 * of that instance's content — from `fallbackHandle` when the owning floor is itself instance-scoped,
 * otherwise from the room the member sits in. Elsewhere lumber stays individually selectable.
 */
function framingMemberAttribution(
  rail: LumberRail,
  regions: readonly FloorRoomRegion[],
  fallbackHandle: DocEntityHandle | undefined,
): FramingMemberAttribution {
  const [x, y] = polyLine3EvalEdge(rail.rail, 0, 0.5, true);
  const region = floorRegionForPoint(x, y, regions);
  const instanceHandle =
    fallbackHandle?.type === "COMPONENT_INSTANCE" ? fallbackHandle : region?.componentInstanceHandle;
  return {
    pickHandle: instanceHandle ?? generatedBuildingEntityHandle("LUMBER", rail.id),
    reportingUnitHandle: region ? region.reportingUnitHandle : fallbackHandle,
  };
}

const floorFramingMaterialSrcs: Record<FloorFramingComponentType, MaterialSrc> = {
  [FloorFramingComponentType.RIM_JOIST]: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Brown },
  [FloorFramingComponentType.FLOOR_JOIST]: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Brown },
  [FloorFramingComponentType.FLOOR_BEAM]: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Green },
  [FloorFramingComponentType.HEADER]: { ...MS_Color__C, colorId: DMC.SystemColors.Green },
  [FloorFramingComponentType.TRIMMER]: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Yellow },
  [FloorFramingComponentType.TAIL_JOIST]: { ...MS_Color__C, colorId: DMC.SystemColors.Gray70 },
  [FloorFramingComponentType.NOGGIN]: { ...MS_Color__C, colorId: DMC.SystemColors.Yellow },
};

function getFloorFramingGeometriesFromFloorSystems(
  floorSystems: Table<FloorSystem>,
  rooms: Table<Room>,
  roomsByLevel: Table<string[]>,
  emitter: BuildingGeometryEmitter,
): void {
  for (const levelId in floorSystems) {
    const roomRegions = floorRoomRegions(levelId, rooms, roomsByLevel);
    const floorSystem = floorSystems[levelId];
    for (const roomClassFloors of floorSystem.roomClassFloors) {
      for (const floor of roomClassFloors) {
        if (floor.floorType === FloorType.NONE || !isSolidFramingFloor(floor)) {
          continue;
        }

        for (const { brep, joistPlacement, rail } of extrudeFloorFramingPlacements(floor.floorFraming)) {
          const componentType = floorFramingComponentTypeFromJoistPlacement(joistPlacement);
          const materialSrc = floorFramingMaterialSrcs[componentType] ?? MS_Color_PlanFloor__Default;
          const { pickHandle, reportingUnitHandle } = framingMemberAttribution(
            rail,
            roomRegions,
            floor.docEntityHandle,
          );
          emitter.emit({
            geom: brep,
            faceCategory: FaceCategory.FLOOR_FRAMING,
            threeLayers: buildExteriorLayers(),
            levelId,
            materialSlot: "framing",
            materialSrc,
            uvGen: UVGeneration.GLOBAL_RECT_UV,
            pickHandle,
            entityHandleForSelectingReportingUnit: reportingUnitHandle,
          });
        }
      }
    }
  }
}

function getFloorFramingGeometriesFromFramedFloorAssemblies(
  framedFloorAssemblies: Table<GeneratedFramedFloorAssembly>,
  rooms: Table<Room>,
  roomsByLevel: Table<string[]>,
  emitter: BuildingGeometryEmitter,
): void {
  const roomRegionsByLevel = new Map<string, FloorRoomRegion[]>();
  for (const assemblyKey in framedFloorAssemblies) {
    const assembly = framedFloorAssemblies[assemblyKey];
    let roomRegions = roomRegionsByLevel.get(assembly.levelId);
    if (!roomRegions) {
      roomRegions = floorRoomRegions(assembly.levelId, rooms, roomsByLevel);
      roomRegionsByLevel.set(assembly.levelId, roomRegions);
    }

    for (const member of assembly.framing.flat()) {
      const materialSrc = floorFramingMaterialSrcs[member.type];
      for (const rail of member.rails) {
        const faces = floorFramingFaces(rail, member.profileDescriptor);
        if (faces.length === 0) {
          continue;
        }
        const { pickHandle, reportingUnitHandle } = framingMemberAttribution(
          rail,
          roomRegions,
          assembly.docEntityHandle,
        );
        emitter.emit({
          geom: faceGroupNew(faces),
          faceCategory: FaceCategory.FLOOR_FRAMING,
          threeLayers: buildExteriorLayers(),
          levelId: assembly.levelId,
          materialSlot: "framing",
          materialSrc,
          uvGen: UVGeneration.GLOBAL_RECT_UV,
          pickHandle,
          entityHandleForSelectingReportingUnit: reportingUnitHandle,
        });
      }
    }
  }
}
